import React, { FC, useMemo, useState } from "react";
import "./WsLimitCard.scss";
import { OPEN_LEAVE_CARD_QUERY_PARAM } from "@app/router/queryParams";
import { useActionSheet, useWindows } from "@app/overlay";
import { useLatestCallback } from "@shared/hooks/useLatestCallback";
import { useUrlParamTrigger } from "@shared/hooks/useUrlParamTrigger";
import { CardHeader } from "@shared/ui/card-header";
import type { ActionSheetSection } from "@shared/ui/overlay/action-sheet";
import { DetailList } from "@shared/ui/detail-row";
import { CardLoader } from "@src/components/card-loader";
import {
    BusinessBoStateValue,
    BusinessDivisionValue,
    formatBusinessDateRangeText,
    formatDivisionText,
} from "@src/components/business-data-display";
import { useBoAvOperations } from "@src/api/bo";
import { useRequiredNumberParam } from "@src/hooks/useRequiredNumberParam";
import { useBoOperationConfirm } from "@src/businessModules/WorkShop/shared/bo/useBoOperationConfirm";
import { useBoOperationActionItems } from "@src/businessModules/WorkShop/shared/bo/useBoOperationActionItems";
import { useWsLimitCardMutations } from "./useWsLimitCardMutations";
import { WsLeavesLimitCardList } from "./components/LeavesList/WsLeavesLimitCardList";
import { useWsLimitCardQuery } from "./hooks/useWsLimitCardQuery";
import { useWsLimitCardLeavesQuery } from "./hooks/useWsLimitCardLeavesQuery";
import { wsLimitCardWindows } from "./wsLimitCard.windows";

export const WsLimitCard: FC = () => {
    const code = useRequiredNumberParam("code", "код ЛК");
    const windows = useWindows();
    const actionSheet = useActionSheet();

    const cardQuery = useWsLimitCardQuery(code);
    const limitCard = cardQuery.data;
    const leavesQuery = useWsLimitCardLeavesQuery(code);

    const avOperationsQuery = useBoAvOperations(limitCard?.Code, limitCard != null);

    const [showInfo, setShowInfo] = useState(true);

    const mutations = useWsLimitCardMutations({
        onBoOperationSuccess: () => {
            void cardQuery.refetch();
            void leavesQuery.refetch();
            void avOperationsQuery.refetch();
        },
    });

    const { confirmAndPerform } = useBoOperationConfirm({
        perform: mutations.performBoOperationMutation.mutateAsync,
    });

    const operationItems = useBoOperationActionItems({
        operations: avOperationsQuery.data,
        context: limitCard,
        confirmAndPerform,
        buildParams: (operation, card) => ({
            operation,
            boCode: card.Code,
            documentLabel: `Лимитная карта УП №${card.Num}`,
            fields: [
                { label: "Отправитель", value: formatDivisionText(card.Sender, card.SenderSection) },
                { label: "Получатель", value: formatDivisionText(card.Receiver, card.ReceiverSection) },
                {
                    label: "Период действия",
                    value: formatBusinessDateRangeText(card.StartDate, card.FinishDate, "dateShort"),
                },
            ],
        }),
    });

    const openLeaveFromUrl = useLatestCallback((leaveCodeParam: string) => {
        const leaveCode = Number(leaveCodeParam);
        if (!Number.isFinite(leaveCode) || leaveCode <= 0 || limitCard == null) {
            return;
        }

        wsLimitCardWindows.leave.open(windows, { leaveCode, limitCard });
    });

    useUrlParamTrigger(OPEN_LEAVE_CARD_QUERY_PARAM, openLeaveFromUrl, {
        enabled: limitCard != null,
    });

    const actionSheetSections = useMemo<ActionSheetSection[]>(() => {
        const sections: ActionSheetSection[] = [
            {
                id: "view",
                items: [
                    {
                        type: "toggle",
                        id: "show-bo-info",
                        label: "Информация о БО",
                        description: "Отображать доп. информацию о БО",
                        checked: showInfo,
                        onCheckedChange: setShowInfo,
                    },
                ],
            },
        ];

        if (operationItems.length > 0) {
            sections.push({
                id: "bo",
                items: [
                    {
                        type: "drill-in",
                        id: "bo-operations",
                        label: "Выполнить операцию",
                        description: "Выбрать доступную BO-операцию",
                        sections: [{ id: "bo-operations", items: operationItems }],
                    },
                ],
            });
        }

        return sections;
    }, [operationItems, showInfo]);

    if (cardQuery.isFetching || limitCard == null) {
        return <CardLoader header list />;
    }

    return (
        <>
            <div className="ws_limit_cards_card">
                <div className="ws_limit_cards_card_header_container">
                    <CardHeader
                        text={`Лимитная карта №${limitCard.Num} (Приход)`}
                        buttons={[
                            {
                                type: "icon",
                                text: "Действия",
                                srcIcon: "/images/common/more_vert.svg",
                                onClick: () =>
                                    actionSheet.open({
                                        title: "Действия",
                                        supportingTitle: "Выберите действие",
                                        sections: actionSheetSections,
                                    }),
                            },
                        ]}
                    />
                    {showInfo && (
                        <DetailList className="ws_limit_cards_data">
                            <DetailList.Row
                                label={"Отправитель"}
                                value={
                                    <BusinessDivisionValue division={limitCard.Sender} section={limitCard.SenderSection} />
                                }
                            />
                            <DetailList.Row
                                label={"Получатель"}
                                value={
                                    <BusinessDivisionValue
                                        division={limitCard.Receiver}
                                        section={limitCard.ReceiverSection}
                                    />
                                }
                            />
                            <DetailList.Row
                                label={"Статус"}
                                value={<BusinessBoStateValue state={limitCard.Bo?.State} labelSource="short" />}
                            />
                            <DetailList.Row
                                label={"Период действия"}
                                value={formatBusinessDateRangeText(limitCard.StartDate, limitCard.FinishDate)}
                            />
                        </DetailList>
                    )}
                </div>
                <WsLeavesLimitCardList
                    leavesQuery={leavesQuery}
                    onOpenLeave={(leaveCode) => {
                        wsLimitCardWindows.leave.open(windows, { leaveCode, limitCard });
                    }}
                />
            </div>
        </>
    );
};
